import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import UserRoleForm from './UserRoleForm';

interface AddRoleDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (role: { name: string; description: string; status: 'active' | 'inactive' }) => Promise<void> | void;
}

const AddRoleDialog: React.FC<AddRoleDialogProps> = ({ 
  open, 
  onClose,
  onSave 
}) => {
  // This state is now defined above
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true,
    isSystem: false,
    userCount: 0,
  });
  
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFieldChange = (
    field: 'name' | 'description' | 'isActive' | 'isSystem' | 'userCount',
    value: string | boolean | number
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      return; // Basic validation
    }
    
    setIsSubmitting(true);
    try {
      await onSave({
        name: formData.name,
        description: formData.description,
        status: formData.isActive ? 'active' : 'inactive'
      });
      
      // Reset form on successful save
      setFormData({
        name: '',
        description: '',
        isActive: true,
        isSystem: false,
        userCount: 0,
      });
    } catch (error) {
      console.error('Failed to create role:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof typeof formData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 12,
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Add New Role</Typography>
            <IconButton
              edge="end"
              color="inherit"
              onClick={onClose}
              aria-label="close"
              disabled={isSubmitting}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <UserRoleForm
            role={{
              name: formData.name,
              description: formData.description,
              isActive: formData.isActive,
              isSystem: formData.isSystem,
              userCount: formData.userCount
            }}
            onChange={(field, value) => {
              if (field === 'isActive') {
                handleFieldChange(field, value as boolean);
              } else if (field === 'name' || field === 'description') {
                handleFieldChange(field, value as string);
              }
            }}
            isEditMode={false}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button
            onClick={onClose}
            disabled={isSubmitting}
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isSubmitting || !formData.name.trim()}
            sx={{ minWidth: 100 }}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddRoleDialog;
